import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { History, Eye, RotateCcw, Calendar, User, FileText, ChevronRight, Hash } from 'lucide-react';

const VersionHistoryPanel = ({
    isOpen,
    versions = [],
    onClose,
    onViewSnapshot,
    currentVersion
}) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    // Local helper if util not available
    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('fr-FR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return ReactDOM.createPortal(
        <AnimatePresence>
            <div className="fixed inset-0 z-[9999] bg-slate-900/50 transition-opacity" onClick={onClose} />
            <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="fixed inset-y-0 right-0 z-[10000] w-full max-w-md bg-white shadow-2xl border-l border-slate-200 flex flex-col"
            >
                {/* Standard Enterprise Header */}
                <div className="px-6 py-5 border-b border-slate-200 bg-white">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 text-slate-700 rounded-md border border-slate-200">
                                <History className="w-5 h-5" />
                            </div>
                            <div>
                                <h2 className="text-base font-semibold text-slate-900">
                                    {t('versionManagement.history.title') || 'Historique des versions'}
                                </h2>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {versions.length} {t('versionManagement.history.subtitle') || 'versions enregistrées'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content Stream */}
                <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
                    {versions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center p-8">
                            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4 border border-slate-200">
                                <History className="w-6 h-6 text-slate-400" />
                            </div>
                            <h3 className="text-slate-700 font-medium mb-1">{t('versionManagement.history.emptyTitle')}</h3>
                            <p className="text-sm text-slate-500 max-w-[200px]">{t('versionManagement.history.empty')}</p>
                        </div>
                    ) : (
                        <div className="relative border-l-2 border-slate-200 ml-3 space-y-6 pb-6">
                            {versions.map((version, index) => {
                                const isStrategic = version.updateType === 'strategic';

                                return (
                                    <div key={index} className="relative pl-6">
                                        {/* Classic Timeline Dot */}
                                        <div className={`absolute -left-[7px] top-2 w-3 h-3 rounded-full ring-4 ring-slate-50 ${isStrategic ? 'bg-orange-500' : 'bg-slate-400'
                                            }`} />

                                        {/* Version Card */}
                                        <div className={`bg-white rounded-lg border ${isStrategic ? 'border-orange-200 shadow-sm' : 'border-slate-200'
                                            } p-4 transition-shadow hover:shadow-md`}>

                                            {/* Version Header */}
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="flex items-center gap-2">
                                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${isStrategic
                                                        ? 'bg-orange-50 text-orange-700 border border-orange-200'
                                                        : 'bg-slate-100 text-slate-700 border border-slate-200'
                                                        }`}>
                                                        v{version.version}
                                                    </span>
                                                    {version.version === currentVersion && (
                                                        <span className="text-[10px] font-medium tracking-wide px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-200">
                                                            {t('versionManagement.history.current')}
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="text-xs text-slate-500 flex items-center gap-1.5">
                                                    <Calendar className="w-3.5 h-3.5" />
                                                    {formatDateTime(version.updatedAt)}
                                                </span>
                                            </div>

                                            {/* Justification Content */}
                                            <div className="mb-4">
                                                <h4 className={`text-sm font-semibold mb-1 ${isStrategic ? 'text-slate-900' : 'text-slate-700'}`}>
                                                    {version.updateType === 'creation' ? t('versionManagement.history.initialCreation') : (isStrategic ? t('versionManagement.history.majorVersion') : t('versionManagement.history.minorUpdate'))}
                                                </h4>
                                                <p className="text-[13px] text-slate-600 leading-relaxed">
                                                    {version.justification || <span className="text-slate-400 italic">{t('versionManagement.history.noComment')}</span>}
                                                </p>
                                            </div>

                                            {/* Metadata Footer */}
                                            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                                                <div className="flex items-center gap-1.5 text-slate-500">
                                                    <User className="w-3.5 h-3.5" />
                                                    <span className="text-xs font-medium">
                                                        {version.updatedByName || t('versionManagement.history.user')}
                                                    </span>
                                                </div>

                                                {/* Actions */}
                                                {isStrategic && (
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => onViewSnapshot(version)}
                                                            className="flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-100 hover:bg-blue-100 hover:border-blue-200 px-2.5 py-1.5 rounded-md transition-colors"
                                                        >
                                                            <Eye className="w-3.5 h-3.5" />
                                                            <span>{t('versionManagement.history.snapshot')}</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </motion.div>
        </AnimatePresence>,
        document.body
    );
};

export default VersionHistoryPanel;
